import { ApiError, BROADCAST_EVENTS, DISCORD_CHANNEL, queryKeys, useDiscordChat } from '@ottd/api';
import type { DiscordChatMessage, DiscordChatReaction } from '@ottd/api';
import type { CSSProperties, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { Hint } from '@/components/prototype/hint';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { DefaultSkeleton } from '@/components/prototype/query-state';
import { useEchoChannel } from '@/hooks/use-echo-channel';
import { cn } from '@/lib/utils';
import type { MessageGroup } from '@/pages/home/discord-chat-groups';
import { discordTimestamp, groupMessages, hoverTime } from '@/pages/home/discord-chat-groups';

/** Poll cadence while the socket is down; the scheduler syncs every 15s. */
const FALLBACK_REFETCH_MS = 30_000;

/** Auto-scroll only when the reader is already this close to the newest line. */
const STICK_TO_BOTTOM_PX = 40;

const AVATAR_SIZE = 40;

const EXTERNAL_LINK =
  'font-display text-[11px] uppercase tracking-wide outline-none transition-[filter] hover:brightness-118 focus-visible:ring-2 focus-visible:ring-ring';

interface DiscordChatProps {
  /** Panel sizing from the row it sits in; the message area fills what's left. */
  className?: string;
  /** Rendered instead of the panel once the query has settled without a mirror. */
  fallback?: ReactNode;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return fallback;
}

/**
 * Homepage "Community" panel: a read-only mirror of one Discord channel fed by
 * the backend cache (seeded on mount, refetched on the `discord.chat` ping).
 * Shows a skeleton while loading, `fallback` when the mirror is disabled, and
 * the error inline otherwise. Marked `data-nosnippet` so search engines never
 * quote chat lines even if they render the page.
 */
export function DiscordChat({ className, fallback = null }: DiscordChatProps) {
  const { t } = useTranslation('home');
  const queryClient = useQueryClient();
  const connected = useEchoChannel(DISCORD_CHANNEL, {
    [BROADCAST_EVENTS.discordChat]: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.discord.chat() }),
  });
  const { data, isLoading, isError, error } = useDiscordChat(
    connected ? false : FALLBACK_REFETCH_MS,
  );

  const channel = data?.channel ?? null;
  const messages = data?.messages ?? [];
  const channelLabel = channel?.name ? `#${channel.name}` : t('discord.title');

  if (isLoading) {
    return (
      <BevelWindow
        title={t('discord.title')}
        shadow="sm"
        bodyClassName="p-3"
        className={cn('flex flex-col', className)}
      >
        <DefaultSkeleton rows={5} />
      </BevelWindow>
    );
  }
  if (channel === null && !isError) return fallback;

  return (
    <section
      aria-labelledby="discord-chat-heading"
      className={cn('flex min-w-0 flex-col', className)}
      data-nosnippet
    >
      <BevelWindow
        title={
          <span id="discord-chat-heading">
            {t('discord.titleWithChannel', { channel: channelLabel })}
          </span>
        }
        action={
          channel?.joinUrl ? (
            <a
              href={channel.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${EXTERNAL_LINK} bevel-discord bg-discord -my-0.5 px-2.5 py-0.5 text-white`}
            >
              {t('discord.join')}
            </a>
          ) : null
        }
        bodyClassName="bg-chat-bg flex min-h-0 flex-1 flex-col p-0"
        shadow="sm"
        className="flex min-h-0 flex-1 flex-col"
      >
        {isError ? (
          <p className="text-destructive p-4 font-mono text-sm" role="alert">
            {errorMessage(error, t('discord.error'))}
          </p>
        ) : (
          <MessageList messages={messages} />
        )}
        {channel?.url ? (
          <div className="border-chat-line flex items-center border-t px-4 py-2">
            <a
              href={channel.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${EXTERNAL_LINK} text-orange ml-auto inline-flex items-center gap-1`}
            >
              {t('discord.open', { channel: channelLabel })}
              <ArrowRight className="size-3" aria-hidden />
            </a>
          </div>
        ) : null}
      </BevelWindow>
    </section>
  );
}

function MessageList({ messages }: { messages: DiscordChatMessage[] }) {
  const { t } = useTranslation('home');
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the reader was at the bottom before the last update. Starts true
  // so the first render lands on the newest line.
  const stickToBottom = useRef(true);
  const items = useMemo(() => groupMessages(messages), [messages]);

  const handleScroll = () => {
    const node = scrollRef.current;
    if (!node) return;
    stickToBottom.current =
      node.scrollHeight - node.scrollTop - node.clientHeight < STICK_TO_BOTTOM_PX;
  };

  // Follow new lines only while the reader is already at the bottom, so
  // scrolling back through history is never yanked away.
  useEffect(() => {
    const node = scrollRef.current;
    if (node && stickToBottom.current) node.scrollTop = node.scrollHeight;
  }, [messages]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="scrollbar-discord text-chat-text min-h-0 flex-1 overflow-y-auto py-3 font-sans text-[15px] leading-[1.375rem]"
      role="log"
      aria-label={t('discord.chatLabel')}
    >
      {items.length === 0 ? (
        <p className="text-chat-muted px-4">{t('discord.empty')}</p>
      ) : (
        <ol>
          {items.map((item) =>
            item.kind === 'date' ? (
              <DateDivider key={item.key} label={item.label} />
            ) : (
              <MessageGroupItem key={item.key} group={item} />
            ),
          )}
        </ol>
      )}
    </div>
  );
}

function DateDivider({ label }: { label: string }) {
  return (
    <li aria-hidden className="mx-4 my-3 flex items-center gap-2">
      <span className="bg-chat-line h-px flex-1" />
      <span className="text-chat-muted text-xs font-semibold">{label}</span>
      <span className="bg-chat-line h-px flex-1" />
    </li>
  );
}

/**
 * One Discord message group: avatar + name/time header on the first line,
 * follow-up lines indented under it with their time revealed on hover.
 */
function MessageGroupItem({ group }: { group: MessageGroup }) {
  const { t } = useTranslation('home');
  const [first, ...rest] = group.messages;
  if (!first) return null;
  return (
    <li className="mt-3 first:mt-0">
      <MessageLine className="pt-0.5">
        <PlayerAvatar
          name={group.author.name}
          src={group.author.avatar}
          size={AVATAR_SIZE}
          className="mt-0.5 rounded-full border-0"
          decorative
        />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span
              className="chat-role font-medium"
              style={
                group.author.color ? ({ '--role': group.author.color } as CSSProperties) : undefined
              }
            >
              {group.author.name}
            </span>
            {group.isBot ? (
              <span className="bg-discord rounded-[3px] px-1 text-[10px] font-semibold uppercase leading-4 text-white">
                {t('discord.bot')}
              </span>
            ) : null}
            {group.createdAt ? (
              <time dateTime={group.createdAt} className="text-chat-muted text-xs">
                {discordTimestamp(group.createdAt)}
              </time>
            ) : null}
          </p>
          <MessageBody message={first} />
        </div>
      </MessageLine>
      {rest.map((message) => (
        <MessageLine key={message.id}>
          <span className="text-chat-muted w-10 shrink-0 self-start pt-1 text-right text-[10px] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
            {message.createdAt ? (
              <time dateTime={message.createdAt}>{hoverTime(message.createdAt)}</time>
            ) : null}
          </span>
          <div className="min-w-0 flex-1">
            <MessageBody message={message} />
          </div>
        </MessageLine>
      ))}
    </li>
  );
}

function MessageLine({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'hover:bg-chat-hover group flex items-start gap-3 px-4 py-0.5 transition-colors',
        className,
      )}
    >
      {children}
    </div>
  );
}

function MessageBody({ message }: { message: DiscordChatMessage }) {
  const { t } = useTranslation('home');

  return (
    <>
      <p className="whitespace-pre-wrap break-words">
        {message.content}
        {message.attachments > 0 ? (
          <span className="bg-chat-line text-chat-muted ml-1 inline-flex items-center gap-1 rounded-[3px] px-1.5 text-xs">
            📎 {t('discord.attachments', { count: message.attachments })}
          </span>
        ) : null}
      </p>
      {message.reactions.length > 0 ? <Reactions reactions={message.reactions} /> : null}
    </>
  );
}

/** Discord's reaction pills: emoji + count, read-only here. */
function Reactions({ reactions }: { reactions: DiscordChatReaction[] }) {
  const { t } = useTranslation('home');

  return (
    <ul className="mt-1 flex flex-wrap gap-1" aria-label={t('discord.reactions')}>
      {reactions.map((reaction) => (
        <Hint key={reaction.key} label={reaction.image ? `:${reaction.name}:` : null}>
          <li
            className="bg-chat-pill border-chat-line text-chat-muted inline-flex items-center gap-1.5 rounded-lg border px-1.5 py-0.5 text-sm leading-4"
            aria-label={t('discord.reaction', { name: reaction.name, count: reaction.count })}
          >
            {reaction.image ? (
              <img
                src={reaction.image}
                alt=""
                width={16}
                height={16}
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span aria-hidden>{reaction.emoji}</span>
            )}
            <span className="text-xs font-medium" aria-hidden>
              {reaction.count}
            </span>
          </li>
        </Hint>
      ))}
    </ul>
  );
}
